import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  IActionType,
  VARIABLE_TEXT_FEATURE_FLAG_ALL,
  VariableSelectFeatureFlag,
  VariableTextFeatureFlag,
} from '@makyoapp/makyo-common'

import {
  BuilderComponentGroup,
  BuilderComponentSettingType,
  ComponentSettingKey,
  MdShape,
  SpacingInputUnit,
} from '../types'
import { LABELS } from '../types/labels'
import { makeClampWidthItems, makeSpacing, makeTextColorSettings } from '../utils/components'

export const VisualComponents: BuilderComponentGroup = {
  id: 'visual',
  title: LABELS.VISUAL_KEY,
  components: [
    // map
    {
      compId: 'map',
      title: LABELS.MAP_KEY,
      searchKeywords: ['map', 'gps', 'location', 'marker', 'address map', 'directions', 'interactive map'],
      icon: 'Marker24',
      nested: false,
      //detailDataSource: undefined,
      settings: {
        data: [
          {
            id: ComponentSettingKey.LocationDataAddress,
            type: BuilderComponentSettingType.VariableSelect,
            label: LABELS.COORDINATE_KEY,
            defaultValue: {
              flag: VariableSelectFeatureFlag.CustomText,
              value: '37.8199109,-122.4785598',
            },
            variableSelectSettings: {
              flags:
                VariableSelectFeatureFlag.SingleCell |
                VariableSelectFeatureFlag.CurrentRow |
                VariableSelectFeatureFlag.CustomText,
              columnTypes: [ColumnTypes.Coordinate],
            },
          },
          {
            id: ComponentSettingKey.LocationDataMarker,
            type: BuilderComponentSettingType.VariableSelect,
            label: LABELS.MARKER_ICON_KEY,
            defaultValue: {
              flag: VariableSelectFeatureFlag.Icon,
              value: 'Place',
            },
            variableSelectSettings: {
              flags:
                VariableSelectFeatureFlag.SingleCell |
                VariableSelectFeatureFlag.CurrentRow |
                VariableSelectFeatureFlag.Icon,
              columnTypes: [ColumnTypes.Image, ColumnTypes.URL, ColumnTypes.Icon, ColumnTypes.Lookup],
            },
          },
          {
            id: ComponentSettingKey.LocationDataHeader,
            type: BuilderComponentSettingType.VariableText,
            label: LABELS.HEADER_KEY,
            defaultValue: [
              {
                type: 'paragraph',
                children: [{ text: 'Golden Gate Bridge' }],
              },
            ],
            variableTextSettings: {
              flags:
                VARIABLE_TEXT_FEATURE_FLAG_ALL &
                ~VariableTextFeatureFlag.ProfileData &
                ~VariableTextFeatureFlag.Icon &
                ~VariableTextFeatureFlag.Image,
              columnTypes: [ColumnTypes.Text],
            },
          },
          {
            id: ComponentSettingKey.LocationDataSubHeader,
            type: BuilderComponentSettingType.VariableText,
            label: LABELS.SUB_HEADER_KEY,
            defaultValue: [
              {
                type: 'paragraph',
                children: [{ text: 'RG9C+XH Presidio of San Francisco, San Francisco, CA' }],
              },
            ],
            variableTextSettings: {
              flags:
                VARIABLE_TEXT_FEATURE_FLAG_ALL &
                ~VariableTextFeatureFlag.ProfileData &
                ~VariableTextFeatureFlag.Icon &
                ~VariableTextFeatureFlag.Image,
              columnTypes: [ColumnTypes.Text],
            },
          },
          {
            id: ComponentSettingKey.LocationDataDescription,
            type: BuilderComponentSettingType.VariableText,
            label: LABELS.DESCRIPTION_KEY,
            defaultValue: [
              {
                type: 'paragraph',
                children: [
                  {
                    text: 'The Golden Gate Bridge is a suspension bridge spanning the Golden Gate, the one-mile-wide (1.6 km) strait connecting San Francisco Bay and the Pacific Ocean in California, United States.',
                  },
                ],
              },
            ],
            variableTextSettings: {
              flags:
                VARIABLE_TEXT_FEATURE_FLAG_ALL &
                ~VariableTextFeatureFlag.ProfileData &
                ~VariableTextFeatureFlag.Icon &
                ~VariableTextFeatureFlag.Image,
            },
          },
          {
            id: ComponentSettingKey.LocationDataAvatar,
            type: BuilderComponentSettingType.VariableSelect,
            label: LABELS.AVATAR_KEY,
            defaultValue: {
              flag: VariableSelectFeatureFlag.CustomText,
              value: 'https://storage.makyo.me/assets/defaultmap_avatar.jpg',
            },
            variableSelectSettings: {
              flags:
                VariableSelectFeatureFlag.Avatar |
                VariableSelectFeatureFlag.Image |
                VariableSelectFeatureFlag.SingleCell |
                VariableSelectFeatureFlag.CurrentRow |
                VariableSelectFeatureFlag.CustomText,
              columnTypes: [ColumnTypes.Image, ColumnTypes.URL, ColumnTypes.Attachment, ColumnTypes.Lookup],
            },
          },
          {
            id: ComponentSettingKey.LocationDataImage,
            type: BuilderComponentSettingType.VariableSelect,
            label: LABELS.IMAGE_KEY,
            variableSelectSettings: {
              flags:
                VariableSelectFeatureFlag.Image |
                VariableSelectFeatureFlag.SingleCell |
                VariableSelectFeatureFlag.CurrentRow |
                VariableSelectFeatureFlag.CustomText,
              columnTypes: [ColumnTypes.Image, ColumnTypes.URL, ColumnTypes.Attachment, ColumnTypes.Lookup],
            },
            defaultValue: {
              flag: VariableSelectFeatureFlag.CustomText,
              value: 'https://storage.makyo.me/assets/defaultmap_background.jpg',
            },
          },
        ],
        designGroup: {
          [LABELS.COLORS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.LocationDesignScheme,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.INFO_BUBBLE_COLOR_VARIANT_KEY,
                defaultValue: 'primary-90',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.LocationDesignScheme,
                },
              },
              {
                id: ComponentSettingKey.LocationDesignColorMarker,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.COLOR_MARKER_KEY,
                defaultValue: 'primary-90',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.LocationDesignScheme,
                },
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [
              ...makeClampWidthItems(ComponentSettingKey.LocationDesignWidth).items,
              {
                id: ComponentSettingKey.LocationDesignHeight,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.HEIGHT_KEY,
                defaultValue: [640],
                sliderSettings: {
                  max: 980,
                  min: 100,
                  step: 1,
                  unit: 'px',
                },
              },
            ],
          },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.LocationDesignAlignment,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
            ],
          },
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.LocationDesignShape,
                type: BuilderComponentSettingType.Select,
                label: '',
                defaultValue: MdShape.None,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          [LABELS.SPACING_KEY]: makeSpacing({
            margin: {
              id: ComponentSettingKey.LocationDesignMargin,
            },
          }),
        },
        action: [
          {
            id: ComponentSettingKey.LocationActionOnClick,
            label: LABELS.ON_BUBBLE_CLICK_KEY,
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.OpenLink,
                settings: {
                  value: 'https://maps.app.goo.gl/ibxjktgVHAuAFb347',
                },
              },
            ],
            actionTypes: [IActionType.OpenLink],
          },
          // {
          //   id: ComponentSettingKey.LocationActionOnDragEnd,
          //   label: LABELS.ON_MARKER_DROP_KEY,
          //   isSimple: false,
          // },
        ],
        behavior: {
          custom: {
            [LABELS.MAP_OPTIONS_KEY]: {
              items: [
                // {
                //   id: ComponentSettingKey.LocationBehaviorDraggable,
                //   type: BuilderComponentSettingType.Switch,
                //   label: 'Draggable Marker',
                //   defaultValue: true,
                // },
                {
                  id: ComponentSettingKey.LocationBehaviorMapTypeControl,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.MAP_TYPE_CONTROL_KEY,
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.LocationBehaviorFullscreenControl,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.FULLSCREEN_CONTROL_KEY,
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.LocationBehaviorStreetViewControl,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.STREET_VIEW_CONTROL_KEY,
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.LocationBehaviorZoomControl,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.ZOOM_CONTROL_KEY,
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.LocationBehaviorZoomLevel,
                  type: BuilderComponentSettingType.Select,
                  label: LABELS.DEFAULT_ZOOM_KEY,
                  forceHorizontal: true,
                  defaultValue: 'near',
                  selectSettings: {
                    options: [
                      { value: 'near', label: 'Near' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'far', label: 'Far' },
                    ],
                  },
                },
                {
                  id: ComponentSettingKey.LocationBehaviorInfoBubble,
                  type: BuilderComponentSettingType.Select,
                  label: LABELS.INFO_BUBBLE_KEY,
                  forceHorizontal: true,
                  defaultValue: 'default',
                  selectSettings: {
                    options: [
                      { value: 'default', label: 'Default' },
                      { value: 'style-1', label: 'Style 1' },
                      { value: 'style-2', label: 'Style 2' },
                      { value: 'style-3', label: 'Style 3' },
                      { value: 'style-4', label: 'Style 4' },
                    ],
                  },
                },
              ],
            },
          },
          visibility: true,
        },
      },
    },

    // divider
    {
      compId: 'divider',
      title: 'Divider',
      searchKeywords: ['divider', 'line', 'separator', 'rule', 'section break', 'horizontal line'],
      icon: 'Divider16',
      nested: false,
      //detailDataSource: undefined,
      settings: {
        // data: [
        //   {
        //     id: ComponentSettingKey.DividerDataText,
        //     label: LABELS.TEXT_KEY,
        //     type: BuilderComponentSettingType.VariableText,
        //     variableTextSettings: {
        //       flags: VARIABLE_TEXT_FEATURE_FLAG_ALL,
        //     },
        //   },
        // ],
        designGroup: {
          // [LABELS.COLOR_SCHEME_KEY]: {
          //   items: [
          //     // makeScheme(ComponentSettingKey.DividerDesignScheme),
          //     {
          //       id: ComponentSettingKey.DividerDesignColor,
          //       label: LABELS.DIVIDER_COLOR_KEY,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       defaultValue: 'primary',
          //       colorVariantSettings: {
          //         schemeId: ComponentSettingKey.DividerDesignScheme,
          //         fullColor: 'bg',
          //       },
          //     },
          //   ],
          // },
          // [LABELS.STYLE_VARIANT_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.DividerDesignTextStyle,
          //       label: LABELS.STYLE_VARIANT_KEY,
          //       type: BuilderComponentSettingType.Select,
          //       defaultValue: 'normal',
          //       selectSettings: {
          //         options: [
          //           {
          //             value: 'normal',
          //             label: 'Normal',
          //           },
          //           {
          //             value: 'subheader',
          //             label: 'Subheader',
          //           },
          //           {
          //             value: 'chip',
          //             label: 'Chip',
          //           },
          //         ],
          //       },
          //     },
          //   ],
          // },
          // [LABELS.TYPOGRAPHY_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.DividerDesignTitleTypography,
          //       label: LABELS.TEXT_STYLE_KEY,
          //       defaultValue: 'Body Large',
          //       type: BuilderComponentSettingType.TypographySelect,
          //     },
          //   ],
          // },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.DividerDesignComponentAlignment,
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
              },
              // {
              //   id: ComponentSettingKey.DividerDesignTextAlignment,
              //   label: LABELS.TEXT_ALIGNMENT_KEY,
              //   defaultValue: 'center',
              //   type: BuilderComponentSettingType.HorizontalAlignment,
              // },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [
              ...makeClampWidthItems(ComponentSettingKey.DividerDesignWidth).items,
              {
                id: ComponentSettingKey.DividerDesignLineThickness,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.LINE_THICKNESS_KEY,
                sliderSettings: {
                  max: 10,
                  min: 1,
                  step: 1,
                  unit: 'px',
                },
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.DividerDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.MARGIN_KEY,
                defaultValue: {
                  top: {
                    value: 16,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 16,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 16,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 16,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
                // spacingSettings: {
                //   max: {
                //     [SpacingInputUnit.Em]: 4,
                //     [SpacingInputUnit.Rem]: 4,
                //     [SpacingInputUnit.Pixel]: Infinity,
                //     [SpacingInputUnit.Percent]: 10,
                //   },
                // },
              },
            ],
          },
          [LABELS.COLORS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.DividerDesignGroupColor,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  [LABELS.DIVIDER_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.DividerDesignColor,
                      ComponentSettingKey.DividerDesignColorSolid,
                      ComponentSettingKey.DividerDesignColorStartGradient,
                      ComponentSettingKey.DividerDesignColorEndGradient,
                      ComponentSettingKey.DividerDesignColorGradientDirection,
                      ComponentSettingKey.DividerDesignScheme,
                      LABELS.DIVIDER_KEY,
                      'adaptive'
                    ).items,
                  ],
                },
              },
            ],
          },
        },
        //  [
        //   {
        //     id: ComponentSettingKey.DividerDesignInset,
        //     label: 'Inset',
        //     type: BuilderComponentSettingType.Select,
        //     selectSettings: {
        //       options: [
        //         {
        //           value: 'none',
        //           label: 'None',
        //         },
        //         {
        //           value: 'start',
        //           label: 'Start',
        //         },
        //         {
        //           value: 'end',
        //           label: 'End',
        //         },
        //         {
        //           value: 'both',
        //           label: 'Both',
        //         },
        //       ],
        //     },
        //   },
        // ],
        behavior: {
          visibility: true,
        },
      },
    },
  ],
}
